import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Table, Tag, Button, Drawer, Form, Input, Select,
  Typography, Card, Row, Col, Statistic, Space,
  message as antMessage, Spin,
} from 'antd';
import {
  CustomerServiceOutlined, ClockCircleOutlined,
  CheckCircleOutlined, SendOutlined,
} from '@ant-design/icons';
import { theme as antdTheme } from 'antd';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import axios from 'axios';

dayjs.extend(relativeTime);

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const STATUS_COLOR = { open: 'blue', in_progress: 'orange', resolved: 'green', closed: 'default' };
const STATUS_LABEL = { open: 'Open', in_progress: 'In Progress', resolved: 'Resolved', closed: 'Closed' };
const PRIORITY_COLOR = { high: 'red', medium: 'orange', low: 'green' };
const CATEGORY_LABEL = { general: 'General', billing: 'Billing', technical: 'Technical', feature_request: 'Feature Request' };

const AdminSupport = () => {
  const { token: tk } = antdTheme.useToken();
  const [tickets, setTickets]         = useState([]);
  const [total, setTotal]             = useState(0);
  const [loading, setLoading]         = useState(true);
  const [page, setPage]               = useState(1);
  const [statusF, setStatusF]         = useState('');
  const [priorityF, setPriorityF]     = useState('');
  const [searchQ, setSearchQ]         = useState('');
  const [orgs, setOrgs]               = useState([]);
  const [orgF, setOrgF]               = useState('');

  // Drawer state
  const [drawerOpen, setDrawerOpen]   = useState(false);
  const [activeTicket, setActive]     = useState(null);
  const [messages, setMessages]       = useState([]);
  const [threadLoading, setTLoading]  = useState(false);
  const [replyText, setReplyText]     = useState('');
  const [replying, setReplying]       = useState(false);
  const [replyStatus, setReplyStatus] = useState('in_progress');
  const threadEndRef = useRef(null);

  // Load orgs for filter dropdown
  useEffect(() => {
    axios.get('/api/admin/orgs?limit=200', { headers: authHeader() })
      .then(r => setOrgs(Array.isArray(r.data) ? r.data : (r.data?.data || [])))
      .catch(() => {});
  }, []);

  const loadTickets = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const params = { page: p, limit: 50 };
      if (statusF)   params.status   = statusF;
      if (priorityF) params.priority = priorityF;
      if (orgF)      params.org_id   = orgF;
      if (searchQ)   params.search   = searchQ;
      const res = await axios.get('/api/admin/support', { headers: authHeader(), params });
      setTickets(res.data.data);
      setTotal(res.data.total);
      setPage(p);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [statusF, priorityF, orgF, searchQ]);

  useEffect(() => { loadTickets(1); }, [loadTickets]);

  const openTicket = async (ticket) => {
    setActive(ticket);
    setDrawerOpen(true);
    setTLoading(true);
    try {
      const res = await axios.get(`/api/admin/support/${ticket.id}`, { headers: authHeader() });
      setActive(res.data.ticket);
      setMessages(res.data.messages);
      setTickets(prev => prev.map(t => t.id === ticket.id ? { ...t, admin_unread: false } : t));
    } catch (err) { console.error(err); }
    finally { setTLoading(false); }
  };

  useEffect(() => {
    if (threadEndRef.current) threadEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Poll for new messages when drawer is open
  useEffect(() => {
    if (!drawerOpen || !activeTicket) return;
    const id = setInterval(async () => {
      try {
        const res = await axios.get(`/api/admin/support/${activeTicket.id}`, { headers: authHeader() });
        setMessages(res.data.messages);
        setActive(res.data.ticket);
      } catch (_) {}
    }, 30000);
    return () => clearInterval(id);
  }, [drawerOpen, activeTicket]);

  const sendReply = async () => {
    if (!replyText.trim()) return;
    setReplying(true);
    try {
      await axios.post(`/api/admin/support/${activeTicket.id}/reply`, { message: replyText, status: replyStatus }, { headers: authHeader() });
      setReplyText('');
      const res = await axios.get(`/api/admin/support/${activeTicket.id}`, { headers: authHeader() });
      setMessages(res.data.messages);
      setActive(res.data.ticket);
      loadTickets(page);
      antMessage.success('Reply sent');
    } catch (err) {
      antMessage.error(err.response?.data?.message || 'Failed to send reply');
    }
    setReplying(false);
  };

  const updateStatus = async (field, value) => {
    try {
      await axios.put(`/api/admin/support/${activeTicket.id}/status`, { [field]: value }, { headers: authHeader() });
      setActive(prev => ({ ...prev, [field]: value }));
      setTickets(prev => prev.map(t => t.id === activeTicket.id ? { ...t, [field]: value } : t));
    } catch (err) {
      antMessage.error('Failed to update ticket');
    }
  };

  // Stats (from current page data)
  const open_count       = tickets.filter(t => t.status === 'open').length;
  const inprogress_count = tickets.filter(t => t.status === 'in_progress').length;
  const resolved_count   = tickets.filter(t => t.status === 'resolved').length;
  const unread_count     = tickets.filter(t => t.admin_unread).length;

  const columns = [
    {
      title: '', key: 'unread', width: 12,
      render: (_, r) => r.admin_unread ? <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#ef4444', margin: '0 auto' }} /> : null,
    },
    {
      title: 'Subject', key: 'subject',
      render: (_, r) => (
        <Space direction="vertical" size={2}>
          <Text strong style={{ fontSize: 13 }}>{r.subject}</Text>
          <Space size={6}>
            <Tag style={{ fontSize: 11, margin: 0 }}>{CATEGORY_LABEL[r.category] || r.category}</Tag>
            <Text type="secondary" style={{ fontSize: 11 }}>#{r.id}</Text>
          </Space>
        </Space>
      ),
    },
    {
      title: 'Org', dataIndex: 'org_name', width: 140,
      render: n => <Text style={{ fontSize: 12 }}>{n}</Text>,
    },
    {
      title: 'User', dataIndex: 'user_email', width: 180,
      render: e => <Text style={{ fontSize: 12 }}>{e}</Text>,
    },
    {
      title: 'Priority', dataIndex: 'priority', width: 90,
      render: p => <Tag color={PRIORITY_COLOR[p] || 'default'} style={{ textTransform: 'capitalize' }}>{p}</Tag>,
    },
    {
      title: 'Status', dataIndex: 'status', width: 120,
      render: s => <Tag color={STATUS_COLOR[s] || 'default'}>{STATUS_LABEL[s] || s}</Tag>,
    },
    {
      title: 'Last Update', dataIndex: 'last_reply_at', width: 130,
      render: (d, r) => <Text type="secondary" style={{ fontSize: 12 }}>{d ? dayjs(d).fromNow() : dayjs(r.created_at).fromNow()}</Text>,
    },
  ];

  return (
    <div style={{ padding: '0 2px' }}>
      <div style={{ marginBottom: 20 }}>
        <Title level={4} style={{ marginBottom: 4 }}>Support Queue</Title>
        <Text type="secondary">Manage and respond to all customer support tickets.</Text>
      </div>

      {/* Stat cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        {[
          { label: 'Open',        value: open_count,       color: '#3b82f6', icon: <CustomerServiceOutlined /> },
          { label: 'In Progress', value: inprogress_count, color: '#f59e0b', icon: <ClockCircleOutlined /> },
          { label: 'Resolved',    value: resolved_count,   color: '#10b981', icon: <CheckCircleOutlined /> },
          { label: 'Unread',      value: unread_count,     color: '#ef4444', icon: <CustomerServiceOutlined /> },
        ].map(s => (
          <Col xs={12} sm={6} key={s.label}>
            <Card size="small" style={{ borderRadius: 10, border: `1px solid ${tk.colorBorderSecondary}` }}>
              <Statistic
                title={<Text type="secondary" style={{ fontSize: 12 }}>{s.label}</Text>}
                value={s.value}
                valueStyle={{ color: s.color, fontSize: 22, fontWeight: 700 }}
                prefix={React.cloneElement(s.icon, { style: { fontSize: 14, color: s.color, marginRight: 4 } })}
              />
            </Card>
          </Col>
        ))}
      </Row>

      <Card style={{ borderRadius: 12, border: `1px solid ${tk.colorBorderSecondary}` }} styles={{ body: { padding: 0 } }}>
        {/* Toolbar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderBottom: `1px solid ${tk.colorBorderSecondary}`, flexWrap: 'wrap' }}>
          <Input.Search
            placeholder="Search subject or email…"
            allowClear
            style={{ width: 220 }}
            onSearch={v => { setSearchQ(v); }}
            onChange={e => { if (!e.target.value) setSearchQ(''); }}
          />
          <Select value={statusF || 'all'} onChange={v => setStatusF(v === 'all' ? '' : v)} style={{ width: 140 }}>
            <Option value="all">All Statuses</Option>
            <Option value="open">Open</Option>
            <Option value="in_progress">In Progress</Option>
            <Option value="resolved">Resolved</Option>
            <Option value="closed">Closed</Option>
          </Select>
          <Select value={priorityF || 'all'} onChange={v => setPriorityF(v === 'all' ? '' : v)} style={{ width: 130 }}>
            <Option value="all">All Priorities</Option>
            <Option value="high">High</Option>
            <Option value="medium">Medium</Option>
            <Option value="low">Low</Option>
          </Select>
          <Select
            value={orgF || 'all'}
            onChange={v => setOrgF(v === 'all' ? '' : v)}
            style={{ width: 180 }}
            showSearch
            filterOption={(input, option) => (option?.children ?? '').toLowerCase().includes(input.toLowerCase())}
          >
            <Option value="all">All Orgs</Option>
            {orgs.map(o => <Option key={o.id} value={String(o.id)}>{o.name}</Option>)}
          </Select>
        </div>

        <Table
          rowKey="id" dataSource={tickets} columns={columns} loading={loading} size="small"
          onRow={r => ({ onClick: () => openTicket(r), style: { cursor: 'pointer' } })}
          rowClassName={r => r.admin_unread ? 'ant-table-row-selected' : ''}
          pagination={{ current: page, pageSize: 50, total, onChange: p => loadTickets(p), showTotal: n => `${n} tickets`, style: { padding: '12px 16px', margin: 0 } }}
          style={{ borderRadius: '0 0 12px 12px', overflow: 'hidden' }}
        scroll={{ x: 'max-content' }}
        />
      </Card>

      {/* Ticket thread drawer */}
      <Drawer
        title={activeTicket ? (
          <Space direction="vertical" size={2} style={{ width: '100%' }}>
            <Text strong style={{ fontSize: 15 }}>#{activeTicket.id} — {activeTicket.subject}</Text>
            <Space size={8} style={{ flexWrap: 'wrap' }}>
              <Text type="secondary" style={{ fontSize: 12 }}>{activeTicket.org_name}</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>·</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>{activeTicket.user_email}</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>·</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>{CATEGORY_LABEL[activeTicket.category]}</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>·</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>Opened {dayjs(activeTicket.created_at).fromNow()}</Text>
            </Space>
            <Space size={8} style={{ marginTop: 6 }}>
              <Select
                size="small"
                value={activeTicket.status}
                onChange={v => updateStatus('status', v)}
                style={{ width: 130 }}
              >
                <Option value="open">Open</Option>
                <Option value="in_progress">In Progress</Option>
                <Option value="resolved">Resolved</Option>
                <Option value="closed">Closed</Option>
              </Select>
              <Select
                size="small"
                value={activeTicket.priority}
                onChange={v => updateStatus('priority', v)}
                style={{ width: 110 }}
              >
                <Option value="low">Low</Option>
                <Option value="medium">Medium</Option>
                <Option value="high">High</Option>
              </Select>
              <Tag color={STATUS_COLOR[activeTicket.status]}>{STATUS_LABEL[activeTicket.status]}</Tag>
              <Tag color={PRIORITY_COLOR[activeTicket.priority]}>{activeTicket.priority}</Tag>
            </Space>
          </Space>
        ) : 'Support Ticket'}
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setMessages([]); setReplyText(''); }}
        width={620}
        styles={{ body: { padding: 0, display: 'flex', flexDirection: 'column', height: '100%' } }}
      >
        {threadLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><Spin /></div>
        ) : (
          <>
            {/* Message thread */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
              {messages.map(m => {
                const isAdmin = m.sender_role === 'admin';
                return (
                  <div key={m.id} style={{ display: 'flex', flexDirection: 'column', alignItems: isAdmin ? 'flex-end' : 'flex-start', marginBottom: 16 }}>
                    <Text type="secondary" style={{ fontSize: 11, marginBottom: 4 }}>
                      {isAdmin ? '🛡️ You (Admin)' : `👤 ${m.sender_email || 'User'}`} · {dayjs(m.created_at).fromNow()}
                    </Text>
                    <div style={{
                      maxWidth: '80%', padding: '10px 14px', borderRadius: isAdmin ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                      background: isAdmin ? '#dc2626' : tk.colorFillAlter,
                      color: isAdmin ? '#fff' : tk.colorText,
                      fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                    }}>
                      {m.message}
                    </div>
                  </div>
                );
              })}
              <div ref={threadEndRef} />
            </div>

            {/* Reply box */}
            {activeTicket?.status !== 'closed' ? (
              <div style={{ padding: '16px 24px', borderTop: `1px solid ${tk.colorBorderSecondary}`, background: tk.colorBgContainer }}>
                <TextArea
                  rows={3}
                  placeholder="Type your reply to the customer…"
                  value={replyText}
                  onChange={e => setReplyText(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && e.metaKey) sendReply(); }}
                  style={{ borderRadius: 8, marginBottom: 8, resize: 'none' }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Space>
                    <Text type="secondary" style={{ fontSize: 12 }}>Set status to:</Text>
                    <Select value={replyStatus} onChange={setReplyStatus} size="small" style={{ width: 130 }}>
                      <Option value="open">Open</Option>
                      <Option value="in_progress">In Progress</Option>
                      <Option value="resolved">Resolved</Option>
                    </Select>
                  </Space>
                  <Button type="primary" icon={<SendOutlined />} loading={replying} onClick={sendReply} style={{ borderRadius: 8, background: '#dc2626', borderColor: '#dc2626' }}>
                    Send Reply
                  </Button>
                </div>
              </div>
            ) : (
              <div style={{ padding: '16px 24px', textAlign: 'center', borderTop: `1px solid ${tk.colorBorderSecondary}` }}>
                <Text type="secondary">This ticket is closed.</Text>
              </div>
            )}
          </>
        )}
      </Drawer>
    </div>
  );
};

export default AdminSupport;
